import React, { useState } from 'react';
import { AlertTriangle, Info, MapPin, Home, Activity, Wind, ThermometerSun, Users, Building, Shield, Heart, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import BookingModal from './BookingModal';

interface SearchResultProps {
  address: string;
  risk: 'low' | 'medium' | 'high';
  level?: number;
  onClose: () => void;
  city?: string;
  province?: string;
  homes?: number;
}

const SearchResult = ({ address, risk, level = 150, onClose, city, province, homes }: SearchResultProps) => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  const getRiskColor = () => {
    switch (risk) {
      case 'high': return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-600', gradient: ['#fee2e2', '#ef4444'] };
      case 'medium': return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-600', gradient: ['#fef3c7', '#f59e0b'] };
      case 'low': return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-600', gradient: ['#dcfce7', '#22c55e'] };
    }
  };

  const getRiskText = () => {
    switch (risk) {
      case 'high': return {
        title: 'Higher Potential Risk Area',
        message: "Based on Health Canada data, your area shows elevated radon potential. While this doesn't guarantee high levels in your home, testing is strongly recommended.",
        recommendation: "Professional testing is the only way to know your actual radon levels. Even homes next to each other can have dramatically different readings. Your results are unique to your property."
      };
      case 'medium': return {
        title: 'Moderate Potential Risk Area',
        message: 'Your area has shown moderate radon levels in previous testing. Consider testing to understand your specific exposure level.',
        recommendation: "Every building has unique radon levels. Testing is the only way to determine your actual exposure and protect your health over the long term."
      };
      case 'low': return {
        title: 'Lower Potential Risk Area',
        message: 'While your area typically shows lower radon levels, testing is still valuable as levels can vary significantly between homes.',
        recommendation: "Even in lower-risk areas, individual buildings can have elevated levels. Testing provides certainty and peace of mind for you and your family."
      };
    };
  };

  // Seasonal variation data based on typical patterns
  const monthlyData = [
    { month: 'Jan', level: level * 1.2, outdoor: level * 0.3 },
    { month: 'Feb', level: level * 1.1, outdoor: level * 0.25 },
    { month: 'Mar', level: level * 0.9, outdoor: level * 0.2 },
    { month: 'Apr', level: level * 0.8, outdoor: level * 0.2 },
    { month: 'May', level: level * 0.7, outdoor: level * 0.15 },
    { month: 'Jun', level: level * 0.6, outdoor: level * 0.15 },
    { month: 'Jul', level: level * 0.6, outdoor: level * 0.15 },
    { month: 'Aug', level: level * 0.7, outdoor: level * 0.15 },
    { month: 'Sep', level: level * 0.8, outdoor: level * 0.2 },
    { month: 'Oct', level: level * 0.9, outdoor: level * 0.2 },
    { month: 'Nov', level: level * 1.1, outdoor: level * 0.25 },
    { month: 'Dec', level: level * 1.2, outdoor: level * 0.3 },
  ];

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[1000] overflow-y-auto">
      <div className="min-h-screen py-8 px-4 flex items-center justify-center">
        <div className={`w-full max-w-6xl bg-white rounded-xl shadow-2xl border-t-4 ${getRiskColor().text.replace('text', 'border')} relative`}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-xl"
          >
            ×
          </button>

          {/* Important Disclaimer Banner */}
          <div className="bg-amber-50 border-b-2 border-amber-200 p-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-amber-900 text-lg mb-2">Important: This Is NOT Your Actual Result</h3>
                <p className="text-amber-800 leading-relaxed">
                  This estimate is based on regional data from other homes in your area. <strong>Your actual radon levels can only be determined through professional testing of your specific property.</strong> Radon levels vary dramatically from building to building—even between neighbors—due to unique factors like construction, ventilation, and soil conditions. <strong>Testing is always recommended regardless of area estimates.</strong>
                </p>
              </div>
            </div>
          </div>

          {/* Header Section */}
          <div className="p-8 border-b">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                {risk === 'high' ? (
                  <AlertTriangle className={`h-8 w-8 ${getRiskColor().text}`} />
                ) : (
                  <Info className={`h-8 w-8 ${getRiskColor().text}`} />
                )}
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2">{getRiskText().title}</h2>
                <div className="flex flex-col space-y-2">
                  <div className="flex items-center text-gray-600">
                    <MapPin className="h-5 w-5 mr-2" />
                    <p className="text-lg">{address}</p>
                  </div>
                  {city && province && (
                    <div className="flex items-center text-gray-600">
                      <Building className="h-5 w-5 mr-2" />
                      <p className="text-lg">{city}, {province}</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-shrink-0 text-center">
                <div className={`text-4xl font-bold ${getRiskColor().text}`}>
                  {level}
                  <span className="text-base ml-1">Bq/m³</span>
                </div>
                <p className="text-sm text-gray-500">Average Level</p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-8">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { icon: Activity, label: 'Risk Level', value: risk.charAt(0).toUpperCase() + risk.slice(1) },
                { icon: Users, label: 'Homes Tested', value: homes?.toLocaleString() || 'N/A' },
                { icon: Wind, label: 'Ventilation Impact', value: 'High' },
                { icon: ThermometerSun, label: 'Seasonal Variance', value: '±40%' },
              ].map((metric, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <metric.icon className={`h-6 w-6 ${getRiskColor().text}`} />
                    <div>
                      <p className="text-sm text-gray-500">{metric.label}</p>
                      <p className="text-lg font-semibold">{metric.value}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Seasonal Variation Chart */}
              <div className="bg-white p-6 rounded-lg border">
                <h3 className="text-lg font-semibold mb-4">Seasonal Variation</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <defs>
                        <linearGradient id="colorLevel" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={getRiskColor().gradient[1]} stopOpacity={0.8}/>
                          <stop offset="95%" stopColor={getRiskColor().gradient[0]} stopOpacity={0.2}/>
                        </linearGradient>
                      </defs>
                      <Area 
                        type="monotone" 
                        dataKey="level" 
                        stroke={getRiskColor().gradient[1]} 
                        fill="url(#colorLevel)"
                        name="Indoor Level"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="outdoor" 
                        stroke="#94a3b8" 
                        fill="#f1f5f9"
                        name="Outdoor Level"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Health Impact Info */}
              <div className="bg-white p-6 rounded-lg border">
                <h3 className="text-lg font-semibold mb-4">Understanding Your Results</h3>
                <div className="space-y-4">
                  <p className="text-gray-600">{getRiskText().message}</p>
                  <div className="mt-4">
                    <h4 className="font-semibold mb-2">Health Canada Guidelines:</h4>
                    <ul className="space-y-2">
                      <li className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                        <span>Below 100 Bq/m³: No action required</span>
                      </li>
                      <li className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                        <span>100-200 Bq/m³: Consider mitigation</span>
                      </li>
                      <li className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                        <span>Above 200 Bq/m³: Mitigation recommended</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Why Testing Matters */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-lg border border-blue-100">
              <h3 className="text-xl font-bold mb-4 text-gray-900">Why Professional Testing Matters</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="flex flex-col items-start">
                  <div className="flex items-center mb-2">
                    <Shield className="h-6 w-6 text-blue-600 mr-2" />
                    <h4 className="font-semibold text-gray-900">Long-Term Protection</h4>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Radon is the leading cause of lung cancer among non-smokers. Whether you spend years in your home or workplace, knowing your levels protects your health over time.
                  </p>
                </div>
                <div className="flex flex-col items-start">
                  <div className="flex items-center mb-2">
                    <Heart className="h-6 w-6 text-red-600 mr-2" />
                    <h4 className="font-semibold text-gray-900">Peace of Mind</h4>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    For a minimal investment, you gain certainty about your environment. Testing removes the guesswork and helps you make informed decisions about your property.
                  </p>
                </div>
                <div className="flex flex-col items-start">
                  <div className="flex items-center mb-2">
                    <Clock className="h-6 w-6 text-green-600 mr-2" />
                    <h4 className="font-semibold text-gray-900">Smart Investment</h4>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    The cost of testing is minimal compared to the value of protecting your health. If issues are found, early detection means easier and more cost-effective solutions.
                  </p>
                </div>
              </div>
              <div className="bg-white p-4 rounded border border-blue-200">
                <p className="text-gray-800 text-sm leading-relaxed">
                  <strong>For homes and commercial properties:</strong> If you spend significant time in your building—whether living, working, or operating a business—testing ensures a healthy environment for you, your family, employees, or customers. Radon levels can change over time, making periodic testing a wise practice.
                </p>
              </div>
            </div>

            {/* Recommendations Section */}
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-red-600">
              <h3 className="text-lg font-semibold mb-4">Next Steps: Get Your Actual Results</h3>
              <p className="text-gray-700 mb-4">{getRiskText().recommendation}</p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setIsBookingModalOpen(true)}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700"
                >
                  Schedule Professional Testing
                </button>
                <a
                  href="/mitigation"
                  className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
                >
                  Learn About Mitigation Options
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        service="Professional Radon Testing"
      />
    </div>
  );
};

export default SearchResult;